import { IntegrationOption } from "@nova/component-export-download-buttons-hover"
import { InspectorPanelAccordion } from "../InspectorPanelAccordion"
import { Fragment, useMemo, useState } from "react"
import { ButtonStatus, ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { Integration } from "@nova/constants"
import { getDate } from "../../module"
import { useAppSelector } from "integrations/redux/hooks"
import {
    selectWorkspaceDateFormat,
    selectWorkspaceTimeFormat,
} from "features/data-management/workspace/WorkspaceManagementSlice"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { selectActivatedFireId } from "features/map/control/selectors"
import classNames from "classnames"
import { useFullnameForUserId } from "features/data-management/users/storage"
import { selectEntityById } from "panels/content/side-panel/slice"
import { InfraredFlight } from "@nova/protobuf"

export const InspectorPanelAccordionIntegrations = ({
    id,
    integrationOptions,
}: {
    id: string
    integrationOptions: Record<string, IntegrationOption> | undefined
}) => {
    const entity = useAppSelector(selectEntityById(id)) as InfraredFlight
    const projectId = useAppSelector(selectActivatedFireId)
    const { isEditor } = useWorkspacePermissions(projectId)

    const [isOpen, setIsOpen] = useState(false)
    const [integrationButtonStatuses, setIntegrationButtonStatuses] = useState<
        Record<string, ButtonStatus>
    >({})

    const updateIntegrationButtonStatus = (id: string, status: ButtonStatus) => {
        setIntegrationButtonStatuses((prev) => ({ ...prev, [id]: status }))
    }

    const integrationMetadata = useMemo(
        () =>
            !!entity.integrations
                ? (JSON.parse(entity.integrations) as Record<string, Integration>)
                : undefined,
        [entity.integrations]
    )

    if (!integrationOptions || !Object.values(integrationOptions).length || !isEditor) return null

    return (
        <InspectorPanelAccordion title="Integrations" isOpen={isOpen} setIsOpen={setIsOpen}>
            <div className="flex flex-col gap-2">
                {Object.entries(integrationOptions).map(([title, integration], i) => (
                    <InspectorPanelAccordionIntegrationOption
                        title={title}
                        {...integration}
                        key={i}
                        integrationKey={integration.key}
                        index={i}
                        integrationMetadata={integrationMetadata}
                        integrationButtonStatuses={integrationButtonStatuses}
                        updateIntegrationButtonStatus={updateIntegrationButtonStatus}
                    />
                ))}
            </div>
        </InspectorPanelAccordion>
    )
}

const InspectorPanelAccordionIntegrationOption = ({
    title,
    callback,
    successMessage,
    failureMessage,
    integrationKey,
    index,
    integrationMetadata,
    integrationButtonStatuses,
    updateIntegrationButtonStatus,
    ExtraInfo,
}: {
    title: string
    integrationKey: string
    index: number
    integrationMetadata?: Record<string, Integration>
    integrationButtonStatuses: Record<string, ButtonStatus>
    updateIntegrationButtonStatus: (id: string, status: ButtonStatus) => void
} & Omit<IntegrationOption, "key">) => {
    const userId = integrationMetadata?.[integrationKey]?.updatedBy.userId
    const { fullName } = useFullnameForUserId(userId)

    const workspaceDateFormat = useAppSelector(selectWorkspaceDateFormat)
    const workspaceTimeFormat = useAppSelector(selectWorkspaceTimeFormat)

    return (
        <Fragment key={title}>
            <ButtonTertiary
                key="button"
                title={title}
                succeededTitle={successMessage}
                failedTitle={failureMessage}
                RightIcon={Icons.Upload}
                className={classNames("w-full", { "mt-2": index > 0 }, "h-fit min-h-[2.75rem]")}
                status={integrationButtonStatuses[title]}
                spinnerColors={{ primary: "#27BBFF", background: "#D1D5DB" }} // blue-500, gray-50
                onClick={async () => {
                    updateIntegrationButtonStatus(title, ButtonStatus.Loading)
                    try {
                        await callback()

                        updateIntegrationButtonStatus(title, ButtonStatus.Succeeded)
                    } catch (error) {
                        console.error(error)
                        updateIntegrationButtonStatus(title, ButtonStatus.Failed)
                    }
                }}
            />
            {integrationMetadata && integrationMetadata[integrationKey] && (
                <div className="flex flex-col">
                    <div className="flex justify-between items-center">
                        <small className="text-gray-50 select-none">Last Sent:</small>
                        <small className="text-gray-300">
                            {getDate(
                                integrationMetadata[integrationKey].updatedAt,
                                workspaceDateFormat,
                                workspaceTimeFormat
                            )}
                        </small>
                    </div>
                    {fullName && (
                        <div className="flex justify-between items-center">
                            <small className="text-gray-50 select-none">Sent By:</small>
                            <small className="text-gray-300">{fullName}</small>
                        </div>
                    )}
                </div>
            )}
            {ExtraInfo && <ExtraInfo />}
        </Fragment>
    )
}
